import { useMsal } from '@azure/msal-react';
import { Button, IconButton, InputAdornment, TextField } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import type { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BeatLoader, HashLoader } from 'react-spinners';
import AlertMessage from '../common/AlertMessage/AlertMessage';

import BrandLogo from '../../branding/BrandLogo';
import { useAzureLogin } from '../../hooks/useAzureLogin';
import { useGoogleLogin } from '../../hooks/useGoogleLogin';
import { mfaDispatchApiCall, mfaVerifyApiCall } from '../../services/api/users';
import { completeLoginWithAccessToken, loginAction } from '../../store/actions/login';
import type { MfaChannel, MfaRequiredPayload, MfaSetupRequiredPayload } from '../../types/authMfa';
import { getToken, removeToken, setToken } from '../../utils/security/tokenStorage';
import { isEnrollmentOnlyToken } from '../../utils/helpers/validateuser';
import { APP_ROUTES } from '../../utils/constants/appRoutes';
import { buildSupportMailtoHref } from '../../utils/constants/support';
import LegalLinks from '../legal/LegalLinks';
import {
  getFullPath,
  getSafeRedirectPath,
  REDIRECT_QUERY_PARAM,
} from '../../utils/redirectAfterLogin';
import EyeHiddenIcon from '../Icons/EyeHiddenIcon';
import EyeIcon from '../Icons/EyeIcon';
import MicrosoftIcon from '../Icons/MicrosoftIcon';
import GoogleSignInButton from './GoogleSignInButton';
import MfaEnrollmentSetup from './MfaEnrollmentSetup';
import MfaVerifyStep from './MfaVerifyStep';
import { ssoButtonSx } from './ssoButtonStyles';
import './style.css';

interface Credentials {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateFrom = (location.state as { from?: { pathname: string; search?: string; hash?: string } })?.from;
  const rawReturnPath =
    (stateFrom ? getFullPath(stateFrom) : null) ||
    new URLSearchParams(location.search).get(REDIRECT_QUERY_PARAM) ||
    '';
  const redirectPath = getSafeRedirectPath(rawReturnPath, APP_ROUTES.HOME);
  const [credentials, setCredentials] = useState<Credentials>({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingAzure, setLoadingAzure] = useState<boolean>(false);
  const [loadingGoogle, setLoadingGoogle] = useState<boolean>(false);
  const [googlePickerOpen, setGooglePickerOpen] = useState(false);
  const [alert, setAlert] = useState<{ show: boolean; type: 'success' | 'error'; message: string }>(
    {
      show: false,
      type: 'success',
      message: '',
    }
  );
  const [mfaSession, setMfaSession] = useState<MfaRequiredPayload | null>(null);
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [mfaDispatching, setMfaDispatching] = useState(false);
  const [enrollmentMode, setEnrollmentMode] = useState(false);

  const { inProgress } = useMsal();
  const dispatch = useDispatch() as ThunkDispatch<unknown, unknown, AnyAction>;

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const isFormValid = useCallback((): boolean => {
    return credentials.email.length > 0 && credentials.password.length > 0;
  }, [credentials]);

  const onMfaRequired = useCallback((payload: MfaRequiredPayload) => {
    setMfaSession(payload);
  }, []);

  const onMfaSetupRequired = useCallback((payload: MfaSetupRequiredPayload) => {
    try {
      setToken(payload.accessToken);
      setEnrollmentMode(true);
    } catch {
      setAlert({
        show: true,
        type: 'error',
        message: 'Could not start verification setup. Please try again.',
      });
    }
  }, []);

  useEffect(() => {
    const t = getToken();
    if (isEnrollmentOnlyToken(t)) {
      setEnrollmentMode(true);
    }
  }, []);

  const exitEnrollment = useCallback(() => {
    removeToken();
    setEnrollmentMode(false);
  }, []);

  const handleEnrollmentFullSession = useCallback(
    async (accessToken: string) => {
      await completeLoginWithAccessToken(
        accessToken,
        dispatch,
        navigate,
        message => {
          setAlert({ show: true, type: 'error', message });
        },
        redirectPath
      );
      setEnrollmentMode(false);
    },
    [dispatch, navigate, redirectPath]
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      setLoading(true);

      dispatch(
        loginAction(
          credentials,
          setLoading,
          (errorMessage: string) => {
            setAlert({
              show: true,
              type: 'error',
              message: errorMessage,
            });
          },
          navigate,
          redirectPath,
          onMfaRequired,
          onMfaSetupRequired
        )
      );
    },
    [dispatch, credentials, redirectPath, onMfaRequired, onMfaSetupRequired]
  );

  const showAuthError = useCallback((errorMessage: string) => {
    setAlert({
      show: true,
      type: 'error',
      message: errorMessage,
    });
  }, []);

  const handleAzureLogin = useAzureLogin(
    setLoadingAzure,
    showAuthError,
    redirectPath,
    onMfaRequired,
    onMfaSetupRequired
  );

  const handleGoogleCredential = useGoogleLogin(
    setLoadingGoogle,
    showAuthError,
    redirectPath,
    onMfaRequired,
    onMfaSetupRequired
  );

  const clearMfaSession = useCallback(() => {
    setMfaSession(null);
  }, []);

  const handleMfaSubmit = useCallback(
    async (channel: MfaChannel, code: string) => {
      if (!mfaSession) return;
      setMfaVerifying(true);
      try {
        const res = await mfaVerifyApiCall({
          mfaContextToken: mfaSession.mfaContextToken,
          channel,
          code,
        });
        await completeLoginWithAccessToken(
          res.data.accessToken,
          dispatch,
          navigate,
          message => {
            setAlert({ show: true, type: 'error', message });
          },
          redirectPath
        );
        setMfaSession(null);
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        const message =
          err.response?.data?.message ||
          'Verification failed. Please try again or sign in from the beginning.';
        setAlert({ show: true, type: 'error', message });
      } finally {
        setMfaVerifying(false);
      }
    },
    [dispatch, mfaSession, navigate, redirectPath]
  );

  const handleMfaDispatch = useCallback(
    async (channel: 'sms' | 'email') => {
      if (!mfaSession) return;
      setMfaDispatching(true);
      try {
        const res = await mfaDispatchApiCall({
          mfaContextToken: mfaSession.mfaContextToken,
          channel,
        });
        setMfaSession(s =>
          s
            ? {
                ...s,
                mfaChallenge: {
                  ...s.mfaChallenge,
                  ...res.data,
                },
              }
            : null
        );
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        setAlert({
          show: true,
          type: 'error',
          message: err.response?.data?.message || 'Could not send verification code. Try again.',
        });
        throw error;
      } finally {
        setMfaDispatching(false);
      }
    },
    [mfaSession]
  );

  const isLockedAccountError =
    alert.show && alert.type === 'error' && alert.message.toLowerCase().includes('locked');
  const isNotAuthorizedGoogleError =
    alert.show &&
    alert.type === 'error' &&
    alert.message.toLowerCase().includes('not authorized to access this application');
  const ssoBusy = inProgress === 'login' || loadingAzure || loadingGoogle || googlePickerOpen;

  return (
    <div className="limiter">
      <AlertMessage
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(prev => ({ ...prev, show: false }))}
      />
      {isLockedAccountError && (
        <p className="auth-nav-link" style={{ textAlign: 'center', marginTop: 8, fontSize: 14 }}>
          Check your email for unlock instructions or contact support.
        </p>
      )}
      {isNotAuthorizedGoogleError && (
        <p className="auth-nav-link" style={{ textAlign: 'center', marginTop: 8, fontSize: 14 }}>
          Google sign-in does not create an account. Ask an administrator to provision your portal
          user with a matching contact email.
        </p>
      )}
      <div className="container-login100">
        <div className="wrap-login100">
          <div>
            <BrandLogo />
            {enrollmentMode ? (
              <MfaEnrollmentSetup
                variant="login"
                onFullSession={handleEnrollmentFullSession}
                onBack={exitEnrollment}
              />
            ) : mfaSession ? (
              mfaSession.mfaChallenge.channels.length === 0 ? (
                <>
                  <p style={{ fontSize: 14, marginTop: 16, textAlign: 'center' }}>
                    Two-step verification is required, but no verification method is available for this account.
                    Please contact support.
                  </p>
                  <Button
                    size="small"
                    type="button"
                    variant="text"
                    className="full-width mt-2"
                    onClick={clearMfaSession}
                  >
                    Back to sign in
                  </Button>
                </>
              ) : (
                <>
                  <h2 style={{ fontSize: 18, fontWeight: 600, textAlign: 'center', margin: '12px 0 0' }}>
                    Two-step verification
                  </h2>
                  <MfaVerifyStep
                    key={mfaSession.mfaContextToken}
                    challenge={mfaSession.mfaChallenge}
                    loading={mfaVerifying}
                    dispatchLoading={mfaDispatching}
                    onSubmit={handleMfaSubmit}
                    onDispatch={handleMfaDispatch}
                    onBack={clearMfaSession}
                  />
                </>
              )
            ) : (
              <form onSubmit={handleSubmit}>
                <div>
                  <div className="login-fields-container">
                    <TextField
                      id="outlined-email-input"
                      name="email"
                      size="small"
                      label="Email Address"
                      type="email"
                      value={credentials.email}
                      onChange={handleInputChange}
                      className="login-fields"
                    />
                    <TextField
                      id="outlined-password-input"
                      name="password"
                      size="small"
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={credentials.password}
                      onChange={handleInputChange}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <EyeHiddenIcon /> : <EyeIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      className="login-fields"
                    />
                  </div>
                  <Button
                    size="medium"
                    type="submit"
                    variant="contained"
                    className="full-width login-submit"
                    disabled={!isFormValid() || loading || ssoBusy}
                  >
                    {loading ? <HashLoader size={30} color="#1b6bae" speedMultiplier={0.8} /> : 'Login'}
                  </Button>
                  <div className="divider">
                    <span className="divider-text">or continue with</span>
                  </div>
                  <div className="sso-buttons">
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      type="button"
                      className="full-width sso-button"
                      sx={ssoButtonSx}
                      onClick={handleAzureLogin}
                      disabled={ssoBusy || loading}
                    >
                      {inProgress === 'login' ? (
                        'Login is currently in progress'
                      ) : loadingAzure ? (
                        <>
                          Verifying your credentials&nbsp;
                          <BeatLoader size={8} color="#1b6bae" speedMultiplier={0.8} />
                        </>
                      ) : (
                        <>
                          <span className="sso-button__icon">
                            <MicrosoftIcon />
                          </span>
                          Entra ID
                        </>
                      )}
                    </Button>
                    <GoogleSignInButton
                      onCredential={handleGoogleCredential}
                      onError={showAuthError}
                      onAccountPickerChange={setGooglePickerOpen}
                      disabled={(ssoBusy && !loadingGoogle && !googlePickerOpen) || loading}
                      loading={loadingGoogle}
                    />
                  </div>
                  <div className="auth-nav-links">
                    <div className="auth-nav-link legal-links">
                      <Link to={APP_ROUTES.FORGOT_PASSWORD}>Forgot Password?</Link>
                      <span aria-hidden="true"> · </span>
                      <a href={buildSupportMailtoHref()}>Contact Support</a>
                    </div>
                    <LegalLinks shortLabels />
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
